import { handleError } from '@zyno-io/vue-foundation';
import { shallowReactive } from 'vue';

import { SuiteCrmContactsApi, type ICrmResolveByPhoneContact } from '@/openapi-clients-generated/suite';

import { crmGetContactDisplayName } from './contact.helpers';

/**
 * Phone number → CRM contact resolution, shared by every Talk surface (call history, voicemail,
 * messages, faxes, the dialer). Lookups are batched, cached and reactive: a screen asks for the
 * numbers it just loaded, reads names straight out of the cache, and re-renders when the batch
 * lands.
 *
 * Scope: *historical* records and numbers the user is typing. Live call events (the incoming/active
 * call dock) are enriched with a caller name server-side before the SSE/push fan-out, so they must
 * NOT be resolved here — the name is already on the event.
 */

const RESOLVE_BATCH_DEBOUNCE_MS = 50;
const RESOLVE_BATCH_MAX = 500; // server-enforced limit on resolve-by-phone
const RESOLUTION_TTL_MS = 30 * 60 * 1000;

/** An array = matched contacts, null = looked up with no match, undefined (absent) = not looked up yet. */
export type ContactResolutionResult = ICrmResolveByPhoneContact[] | null;

// E.164 → resolution. Only numbers beginning with '+' are ever looked up: resolve-by-phone matches
// stored numbers exactly, so extensions and display-formatted strings can never match.
// Shallow: the cached contact summaries are read-only to consumers, so there's nothing to gain from
// proxying each one — but map reads/writes still track, which is what re-renders the lists.
const resolutions = shallowReactive(new Map<string, ContactResolutionResult>());

// E.164 → time of last successful resolve. Entries older than RESOLUTION_TTL_MS still serve reads
// (stale-while-revalidate) but re-fetch on the next resolveNumbers call for that number.
const resolvedAt = new Map<string, number>();

const pendingNumbers = new Set<string>();
const inFlightNumbers = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
// Bumped on reset (tenant switch / logout); responses from a previous generation are dropped.
let generation = 0;

function isLookupCandidate(value: string | null | undefined): value is string {
    return !!value && value.startsWith('+');
}

function isFreshlyResolved(number: string): boolean {
    if (!resolutions.has(number)) return false;
    return Date.now() - (resolvedAt.get(number) ?? 0) < RESOLUTION_TTL_MS;
}

function scheduleFlush() {
    // Plain setTimeout rather than a lodash debounce: the visual-test harness freezes Date.now(),
    // which lodash's timers key off — a debounced flush would never fire there.
    if (!flushTimer) flushTimer = setTimeout(flushPending, RESOLVE_BATCH_DEBOUNCE_MS);
}

async function flushPending() {
    flushTimer = null;
    const startedGeneration = generation;
    const batch = [...pendingNumbers].slice(0, RESOLVE_BATCH_MAX);
    if (!batch.length) return;
    for (const phone of batch) {
        pendingNumbers.delete(phone);
        inFlightNumbers.add(phone);
    }
    // More than RESOLVE_BATCH_MAX queued: flush the remainder in a follow-up batch.
    if (pendingNumbers.size) scheduleFlush();

    try {
        const { data } = await SuiteCrmContactsApi.postCrmContactsResolveByPhone({ body: { phones: batch } });
        if (generation !== startedGeneration) return;
        for (const result of data?.results ?? []) {
            resolutions.set(result.phone, result.contacts.length ? result.contacts : null);
            resolvedAt.set(result.phone, Date.now());
        }
    } catch (err) {
        if (generation !== startedGeneration) return;
        // Leave the numbers uncached so a later screen load retries them.
        handleError(err, 'Failed to resolve contacts for phone numbers');
    } finally {
        if (generation === startedGeneration) {
            for (const phone of batch) inFlightNumbers.delete(phone);
        }
    }
}

/**
 * Match label for the contacts sharing one number: "Jane Doe", or "Jane Doe (+2 others)" when
 * several match. Undefined when nothing matched.
 */
export function getContactMatchLabel(contacts: Parameters<typeof crmGetContactDisplayName>[0][]): string | undefined {
    if (!contacts.length) return undefined;
    const name = crmGetContactDisplayName(contacts[0]!) ?? 'Unnamed contact';
    const others = contacts.length - 1;
    return others ? `${name} (+${others} other${others === 1 ? '' : 's'})` : name;
}

export const ContactResolution = {
    /**
     * Queue phone numbers for contact resolution. Non-'+' values (extensions, already-formatted
     * display strings) are ignored. Results land in a reactive map, so templates reading
     * `displayNameForNumber` re-render when they arrive. Entries older than the 30-minute TTL are
     * re-fetched (the stale value keeps serving reads until the refresh lands). `force` refreshes
     * even fresh entries (used for explicit background refreshes, e.g. the dialer).
     */
    resolveNumbers(numbers: (string | null | undefined)[], opts: { force?: boolean } = {}) {
        let queued = false;
        for (const number of numbers) {
            if (!isLookupCandidate(number)) continue;
            if (inFlightNumbers.has(number) || pendingNumbers.has(number)) continue;
            if (!opts.force && isFreshlyResolved(number)) continue;
            pendingNumbers.add(number);
            queued = true;
        }
        if (queued) scheduleFlush();
    },

    /** Resolved contacts for a number: array = matched, null = no match, undefined = unknown. */
    resolutionForNumber(number: string | null | undefined): ContactResolutionResult | undefined {
        if (!isLookupCandidate(number)) return undefined;
        return resolutions.get(number);
    },

    /** First matched contact for a number, if any. */
    contactForNumber(number: string | null | undefined): ICrmResolveByPhoneContact | undefined {
        return this.resolutionForNumber(number)?.[0];
    },

    /**
     * Display name of the first matched contact for a number, if any. Use this where the name is
     * consumed rather than shown — search matching, initials — and `displayLabelForNumber` where it
     * is shown to the user.
     */
    displayNameForNumber(number: string | null | undefined): string | undefined {
        const contact = this.contactForNumber(number);
        return contact ? (crmGetContactDisplayName(contact) ?? undefined) : undefined;
    },

    /**
     * Display label for a number: the first contact's name, suffixed with "(+N other[s])" when
     * several contacts share the number.
     */
    displayLabelForNumber(number: string | null | undefined): string | undefined {
        return getContactMatchLabel(this.resolutionForNumber(number) ?? []);
    },

    /**
     * Drop every cached entry referencing a contact, plus any numbers passed in. Used when a change
     * can't be described locally (a merge, a delete) — the numbers re-resolve on next use.
     */
    invalidateContact(contactId: string, phoneNumbers: string[] = []) {
        for (const [phone, contacts] of resolutions.entries()) {
            if (contacts?.some(c => c.id === contactId)) {
                resolutions.delete(phone);
                resolvedAt.delete(phone);
            }
        }
        for (const phone of phoneNumbers) {
            if (isLookupCandidate(phone)) {
                resolutions.delete(phone);
                resolvedAt.delete(phone);
            }
        }
    },

    /**
     * Seed the cache from a freshly saved contact (create/edit result) so rows update immediately
     * without waiting for a resolve round-trip. A number the contact no longer owns keeps its entry
     * with this contact removed from the match list, and is marked stale so the next resolve
     * refreshes it.
     */
    applyContactUpdate(contact: {
        id: string;
        avatarId?: string | null;
        displayName?: string | null;
        firstName?: string | null;
        lastName?: string | null;
        company?: string | null;
        phones?: { phone: string }[];
    }) {
        const summary: ICrmResolveByPhoneContact = {
            id: contact.id,
            avatarId: contact.avatarId ?? null,
            displayName: contact.displayName ?? null,
            firstName: contact.firstName ?? null,
            lastName: contact.lastName ?? null,
            company: contact.company ?? null
        };
        const currentPhones = new Set((contact.phones ?? []).map(p => p.phone).filter(p => isLookupCandidate(p)));

        for (const [phone, contacts] of resolutions.entries()) {
            if (!contacts?.some(c => c.id === contact.id)) continue;
            if (currentPhones.has(phone)) continue;
            // Contact no longer owns this number. Drop it from the match list rather than deleting
            // the entry: deleting reverts the number to "never looked up", and *unknown* is not the
            // same as "no contact" to callers that branch on the difference — the contact action
            // menu then offers neither the contact actions nor Create/Add contact. Nothing
            // re-resolves the number on this path either, so the menu stayed wrong until the list
            // was reloaded. Other contacts may still match, so only this one is removed; clearing
            // resolvedAt marks the entry stale so the next resolveNumbers refreshes it.
            const remaining = contacts.filter(c => c.id !== contact.id);
            resolutions.set(phone, remaining.length ? remaining : null);
            resolvedAt.delete(phone);
        }
        for (const phone of currentPhones) {
            const others = (resolutions.get(phone) ?? []).filter(c => c.id !== contact.id);
            resolutions.set(phone, [summary, ...others]);
            resolvedAt.set(phone, Date.now());
        }
    },

    /** Every contact that owns a number — a number can legitimately belong to more than one. */
    contactsForNumber(number: string | null | undefined): ICrmResolveByPhoneContact[] {
        return this.resolutionForNumber(number) ?? [];
    },

    /** Clear all cached state (tenant switch / logout). */
    reset() {
        generation++;
        if (flushTimer) {
            clearTimeout(flushTimer);
            flushTimer = null;
        }
        pendingNumbers.clear();
        inFlightNumbers.clear();
        resolutions.clear();
        resolvedAt.clear();
    }
};
